import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowRight, Moon, Info, MapPin } from "lucide-react";
import { api, money } from "@/lib/api";
import { COLLECTIONS } from "@/data/collections";
import { DESTINATIONS } from "@/data/destinations";
import { SearchWidget } from "@/components/SearchWidget";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

export default function Collection() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const cfg = COLLECTIONS[slug];
  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/packages").then(({ data }) => {
      let list = data.results;
      if (cfg) list = list.filter(cfg.match);
      if (cfg?.sortByPrice) list = [...list].sort((a, b) => a.price - b.price);
      setPkgs(list);
    }).finally(() => setLoading(false));
  }, [slug]);

  if (slug === "destinations") return <Destinations />;
  if (!cfg) return <div className="max-w-3xl mx-auto px-4 py-24 text-center"><h1 className="font-heading font-black text-3xl">Not found</h1><Link to="/" className="text-brand font-bold mt-4 inline-block">Home</Link></div>;

  return (
    <div>
      <section className="relative h-[360px]">
        <img src={cfg.image} alt={cfg.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/30 to-slate-950/20" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="font-heading font-black text-white text-4xl sm:text-6xl tracking-tight max-w-3xl">{cfg.title}</h1>
              <p className="text-white/85 text-lg mt-3 max-w-2xl">{cfg.subtitle}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-12 relative z-10"><SearchWidget defaultTab="packages" /></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-10" data-testid="collection-grid">
        <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-6">
          <Info className="w-4 h-4 shrink-0" /> Demo inventory — sample holiday prices shown for preview. Live supplier availability connects at launch.
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-2xl" />) :
            pkgs.length === 0 ? <p className="text-slate-500 col-span-full py-10 text-center">No holidays in this collection yet.</p> :
            pkgs.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} data-testid={`collection-card-${i}`}>
                <Link to={`/packages/${p.id}`} className="group block rounded-2xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-2xl transition-all duration-300">
                  <div className="relative h-56 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-brand text-white text-xs font-bold">{p.tag}</span>
                    <div className="absolute bottom-4 left-4 text-white"><div className="text-xs font-bold uppercase tracking-wide opacity-80">{p.country}</div><div className="font-heading font-extrabold text-2xl">{p.destination}</div></div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-1 text-xs font-bold text-emerald-600 mb-2"><Star className="w-3.5 h-3.5 fill-emerald-500 text-emerald-500" />{p.rating} · {p.reviews_count} reviews</div>
                    <h3 className="font-heading font-bold text-xl text-slate-950 leading-snug">{p.title}</h3>
                    <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-2"><Moon className="w-4 h-4" />{p.nights} nights · flights + hotel</div>
                    <div className="mt-5 pt-4 border-t border-slate-100 flex items-end justify-between">
                      <div><span className="text-xs text-slate-400">from</span><div className="font-heading font-black text-2xl text-slate-950">{money(p.price)}<span className="text-sm font-normal text-slate-400"> pp</span></div></div>
                      <span className="text-brand font-bold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">View deal <ArrowRight className="w-4 h-4" /></span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
        </div>
      </div>
    </div>
  );
}

function Destinations() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-heading font-black text-4xl text-slate-950 tracking-tight">Destination Guides</h1>
      <p className="text-slate-500 mt-2 max-w-2xl">Explore our favourite destinations and find the perfect place for your next trip.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {DESTINATIONS.map((d, i) => (
          <motion.div key={d.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
            className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl transition-all">
            <div className="relative h-52 overflow-hidden">
              <img src={d.image} alt={d.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white"><div className="text-xs uppercase tracking-wide opacity-80">{d.tag}</div><div className="font-heading font-extrabold text-2xl">{d.name}</div></div>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="w-4 h-4" />{d.country}</div>
              <div className="mt-4 flex gap-2">
                <Link to={`/hotels?city=${d.name}`} className="flex-1"><Button variant="outline" className="w-full rounded-full">Hotels</Button></Link>
                <Link to={`/packages?destination=${d.name}`} className="flex-1"><Button className="w-full rounded-full bg-brand hover:bg-brand-hover">Holidays</Button></Link>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
